import React, {useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {motion} from 'motion/react';
import {Paper, Text} from '@tenancy.nz/ui';
import {pageView} from '../../analytics';
import Actions from '../../components/Actions';
import DeletedProperty from '../../components/DeletedProperty';
import Footer from '../../components/Footer';
import Nameplate from '../../components/Nameplate';
import NotFound from '../../components/NotFound';
import PageHero from '../../components/PageHero';
import PropertyAttributes from '../../components/PropertyAttributes';
import Section from '../../components/Section';
import SectionHeader from '../../components/SectionHeader';
import {useInitAgencyBySlugQuery} from '../../api/main/agency';
import {
  useBookingByIdQuery,
  useConfirmBookingMutation,
  useCancelBookingMutation,
} from '../../api/bookme/booking';
import {showToast} from '../toast/toastSlice';
import {env} from '../../utils/helpers';

function BookingDetailFeature() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const {id, bookingId} = useParams();

  const [confirmBooking] = useConfirmBookingMutation();
  const [cancelBooking] = useCancelBookingMutation();

  const {
    data: {prepared = {agency: {}, property: {}}} = {},
    isError,
    isLoading,
  } = useInitAgencyBySlugQuery(id, {
    skip: !id,
  });
  const {agency, property} = prepared;

  const {
    data: {prepared: booking} = {prepared: {}},
    isLoading: isLoadingBooking,
    isError: isErrorBooking,
  } = useBookingByIdQuery(
    {id: bookingId, propertyId: property.id},
    {
      skip: !property.id || !bookingId,
    }
  );

  useEffect(() => {
    // Log the page view in analytics
    pageView({
      path: location.pathname,
      title: document.title,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = () =>
    confirmBooking({id: bookingId, propertyId: property.id})
      .unwrap()
      .catch(({message}) =>
        dispatch(
          showToast({
            message: message || 'An error occured while confirming booking.',
            color: 'error',
          })
        )
      );

  const handleCancel = () =>
    cancelBooking({id: bookingId, propertyId: property.id})
      .unwrap()
      .catch(({message}) =>
        dispatch(
          showToast({
            message: message || 'An error occured while cancelling booking.',
            color: 'error',
          })
        )
      );

  const actions = [
    {
      text: 'Confirm Booking',
      onClick: handleConfirm,
      size: 'large',
      slug: 'confirm',
    },
    {
      text: 'View other listings',
      onClick: () => navigate(`/tps${agency.clientCode}`),
      size: 'large',
      slug: 'go_back',
    },
    {
      text: 'Cancel Booking',
      onClick: handleCancel,
      variant: 'outlined',
      color: 'inherit',
      size: 'large',
      slug: 'cancel',
    },
  ]
    .filter(({slug}) => {
      if (slug === 'confirm') {
        return booking.confirmable;
      }
      if (slug === 'cancel') {
        return !booking.cancelled;
      }
      if (slug === 'go_back') {
        return booking.confirmed || booking.cancelled;
      }
      return true;
    })
    .map(({slug, ...rest}) => rest);

  // Initial loading view
  if (isLoading || isLoadingBooking) {
    return <Nameplate skeleton />;
  }

  // If no access, show 404 page
  if (
    !isLoading &&
    !isLoadingBooking &&
    (!agency.hasBookMeAccess || isError || isErrorBooking)
  ) {
    return <NotFound />;
  }

  // Show deleted property page
  if (!isLoading && property.status === 'deleted') {
    return (
      <DeletedProperty
        url={`${env('REACT_APP_URL')}/tps${agency.clientCode}`}
      />
    );
  }

  const headingText = () => {
    if (booking.cancelled) {
      return 'Your booking has been cancelled';
    }
    if (booking.confirmed) {
      return 'Your booking has been confirmed';
    }

    return 'Manage your booking';
  };

  const content = (
    <Paper inset="medium" sx={{p: {md: 8}}}>
      <SectionHeader heading={headingText()} />
      <Text paragraph variant="subtitle1">
        Your appointment details:
      </Text>
      <Text variant="subtitle1" weight="700">
        {booking.scheduledAtDisplay}
      </Text>
      <PropertyAttributes
        address={property.address}
        noOfBedrooms={property.bedrooms}
        noOfBathrooms={property.bathrooms}
        noOfCarparks={property.parkingSpaces}
        sx={{mt: 1, mb: 8}}
      />
      <Actions items={actions} />
    </Paper>
  );

  return (
    <React.Fragment>
      <Nameplate
        heading={agency.companyName}
        logo={agency.logo}
        ctaLabel="Manage your bookings"
        ctaHref={`${env('REACT_APP_TENANT_PORTAL_URL')}/viewings`}
      />
      <PageHero fixed slides={property.images} />
      <motion.div>
        <Section leftContent={content} />
      </motion.div>
      <Footer />
    </React.Fragment>
  );
}

export default BookingDetailFeature;
